import { FC, useCallback, useContext } from 'react';
import { Registration } from '@stax-core/types-omni';
import { coreapi } from '../../api';
import { Checkbox, Field } from '../../components';
import { BackButton } from '../components/BackButton';
import { CenteredContent } from '../components/CenteredContent';
import { NextButton } from '../components/NextButton';
import sellingFrequency from '../constants/sellingFrequency';
import {
  AuthStore,
  RegistrationStore,
  updateRegistration,
} from '../../context';
import { useToaster, useWriteableFieldFormValidation } from '../../hooks';
import { MainForm } from '../components/application/MainForm';

interface FieldValues {
  meta?: Pick<Registration['meta'], 'selling_frequency'>;
}

export const SellingFrequency: FC = () => {
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const { toaster, toast } = useToaster();
  const {
    state: { auth },
  } = useContext(AuthStore);
  const authToken = auth?.token ?? null;

  const validate = useWriteableFieldFormValidation((values: FieldValues) => {
    const errors: Partial<Record<keyof FieldValues, any>> = {};
    if (!values.meta?.selling_frequency) {
      if (!errors.meta) errors.meta = {};
      errors.meta.selling_frequency = 'Selling frequency is required';
    }

    return errors;
  });

  const onSubmit = useCallback(
    async (formValues: FieldValues) => {
      const meta = registration?.meta ?? {};
      const payload = {
        meta: { ...meta, selling_frequency: formValues.meta?.selling_frequency },
      };

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));
        return true;
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toast, toaster, registration],
  );

  return (
    <MainForm<FieldValues>
      initialValues={mapInitialFormValues(registration)}
      validate={validate}
      onSubmit={onSubmit}
    >
      {formProps => (
        <CenteredContent
          id="selling-frequency"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <BackButton id="selling-frequency-back" />
          <h2>How often do you sell online?</h2>
          <div>
            {sellingFrequency.map(reason => (
              <div
                key={reason.value}
                style={{ margin: '16px 0' }}
                data-testid={`application-selling-frequency-radio-${reason.value}`}
              >
                <Field
                  name={`meta.selling_frequency`}
                  label={reason.label}
                  type="radio"
                  value={reason.value}
                >
                  {props => (
                    <>
                      <Checkbox
                        {...props.input}
                        variant={'secondary'}
                        id={`selling-frequency-${reason.value}-value`}
                      />
                    </>
                  )}
                </Field>
              </div>
            ))}
          </div>
          <NextButton id="selling-frequency-continue" formProps={formProps} />
        </CenteredContent>
      )}
    </MainForm>
  );
};

function mapInitialFormValues(
  registration: Registration | null,
): Partial<FieldValues> {
  return registration
    ? {
        meta: { selling_frequency: registration?.meta?.selling_frequency },
      }
    : ({
        meta: { selling_frequency: null },
      } as FieldValues);
}
